import { useCallback, useMemo } from 'react'

import { addDays, format, isValid, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'

import type {
  DateRangePreset,
  DateRangeStringValue,
} from '@/Atomic/Molecules/DateRangeDropdown'
import { useFilterValues } from '@/Components/FormContext'
import {
  DATE_PRESET_VALUES,
  DatePreset,
  getDateRange,
} from '@/utils/datePresets'

function isoToDateString(iso: unknown, dayOffset = 0): string | null {
  if (typeof iso !== 'string' || !iso) {
    return null
  }
  const d = parseISO(iso)
  if (!isValid(d)) {
    return null
  }
  // addDays is calendar-aware (safe across DST), unlike raw ms arithmetic.
  return format(dayOffset ? addDays(d, dayOffset) : d, 'yyyy-MM-dd')
}

/**
 * Write direction. `date` is the inclusive last day the user picked
 * (`YYYY-MM-DD`). PISA's `.lte` bound is exclusive, so the stored ISO must be
 * the *next* midnight — otherwise the whole last day is dropped from results.
 * Same rule as `weekRangeToApiFilters` / `dateLtIso` elsewhere.
 */
export function inclusiveDateToExclusiveIso(date: string): string {
  const d = parseISO(date)
  // Guard mirrors isoToDateString: an invalid input would make toISOString
  // throw a bare RangeError. Fail loud with the offending value instead.
  if (!isValid(d)) {
    throw new Error(`inclusiveDateToExclusiveIso: invalid date "${date}"`)
  }
  return addDays(d, 1).toISOString()
}

/**
 * Read direction. Inverse of {@link inclusiveDateToExclusiveIso}: the stored
 * `.lte` ISO is the exclusive next-midnight bound, so shift back 1 day to the
 * inclusive date the picker displays. Returns null for empty/invalid input.
 */
export function exclusiveIsoToInclusiveDate(iso: unknown): string | null {
  return isoToDateString(iso, -1)
}

function presetToDateStrings(
  preset: string,
): { from: string; to: string } | null {
  const { gteValue, lteValue } = getDateRange(preset)
  if (!gteValue || !lteValue) {
    return null
  }
  const from = isoToDateString(gteValue)
  const to = exclusiveIsoToInclusiveDate(lteValue)
  if (!from || !to) {
    return null
  }
  return { from, to }
}

const PRESET_LABEL_KEYS: Record<DatePreset, string> = {
  today: 'common.filter.today',
  yesterday: 'common.filter.yesterday',
  last7: 'common.filter.last7Days',
  last30: 'common.filter.last30Days',
  last90: 'common.filter.last90Days',
  next7: 'common.filter.next7Days',
  next30: 'common.filter.next30Days',
  next90: 'common.filter.next90Days',
}

export function useDateRangeFilter(fieldPrefix = '') {
  const { t } = useTranslation()
  const { values, setValues } = useFilterValues()
  const fieldKey = fieldPrefix ? `${fieldPrefix}_beginAt` : 'beginAt'

  const gteKey = `${fieldKey}.gte`
  const lteKey = `${fieldKey}.lte`

  const value: DateRangeStringValue = useMemo(
    () => [
      isoToDateString(values[gteKey]),
      exclusiveIsoToInclusiveDate(values[lteKey]),
    ],
    [values, gteKey, lteKey],
  )

  const presets: DateRangePreset[] = useMemo(
    () =>
      DATE_PRESET_VALUES.map((key) => {
        const labelKey = PRESET_LABEL_KEYS[key]
        const dates = presetToDateStrings(key)
        if (!dates || !labelKey) {
          return null
        }
        return { label: t(labelKey), from: dates.from, to: dates.to }
      }).filter((p): p is DateRangePreset => p !== null),
    [t],
  )

  const onChange = useCallback(
    ([from, to]: DateRangeStringValue) => {
      if (!from || !to) {
        setValues((prev) => {
          const { [gteKey]: _g, [lteKey]: _l, ...rest } = prev
          return rest
        })
        return
      }
      setValues((prev) => ({
        ...prev,
        [gteKey]: parseISO(from).toISOString(),
        [lteKey]: inclusiveDateToExclusiveIso(to),
      }))
    },
    [setValues, gteKey, lteKey],
  )

  return { presets, value, onChange }
}
